import StoreOfferPicker from "../components/StoreOfferPicker";
import { groupOffers, selectedOffer, offerTerms } from "../lib/storeOffers";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { Check, X, Crown, Star, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { isNative, platform } from "../lib/native";
import { buy, loadProducts, PRODUCT_BY_PLAN } from "../lib/iap";
import { ACTIVATION_PENDING_MESSAGE } from "../lib/iapDelivery";
import SubscriptionNotice from "../components/SubscriptionNotice";
import { useAuth } from "../context/AuthContext";
import { HitmaxWordmark } from "../components/HitmaxWordmark";

const GREEN = "#17E37E";
const GOLD = "#D9B36A";

// VIP en premier (flagship), puis Pro (recommandé), puis Starter.
const PLANS = [
  {
    id: "vip", name: "VIP", monthly: 29.99, annual: 287.90, gold: true,
    badge: { icon: Crown, textKey: "paywall.badge_vip", color: GOLD },
    lookup: { monthly: "hitmax_vip_monthly", annual: "hitmax_vip_annual" },
    featuresKey: "paywall.vip_features",
    featuresOk: [true, true, true, true, true, true],
  },
  {
    id: "pro", name: "PRO", monthly: 19.99, annual: 189, highlight: true,
    badge: { icon: Star, textKey: "paywall.badge_pro", color: GREEN },
    lookup: { monthly: "hitmax_pro_monthly", annual: "hitmax_pro_annual" },
    featuresKey: "paywall.pro_features",
    featuresOk: [true, true, true, true, true, true, false, false],
  },
  {
    id: "starter", name: "STARTER", monthly: 6.99, annual: 66.99,
    lookup: { monthly: "hitmax_starter_monthly", annual: "hitmax_starter_annual" },
    featuresKey: "paywall.starter_features",
    featuresOk: [true, true, true, true, false, false],
  },
];

const fmt = (n) => Number(n).toFixed(2).replace(".", ",");

export default function Paywall() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { user, refreshUser, subscription, subscriptionError, billingBusy, runBillingAction } = useAuth();
  const native = isNative();
  const [annual, setAnnual] = useState(false);
  const [freeMatch, setFreeMatch] = useState(null);
  const [busy, setBusy] = useState(null);
  const [storeProducts, setStoreProducts] = useState({});
  const [selected, setSelected] = useState({});
  const [storeError, setStoreError] = useState("");

  useEffect(() => {
    api.get("/user/free-trial-status")
      .then((r) => setFreeMatch(r.data.free_trial_match_id || null))
      .catch(() => {});
    if (native) {
      loadProducts()
        .then((list) => {
          setStoreProducts(groupOffers(list));
        })
        .catch((error) => setStoreError(error?.message || "Paiements natifs indisponibles"));
    }
  }, [native]);

  const period = annual ? "annual" : "monthly";
  const priceInfo = (p) => {
    if (annual) return { price: p.annual, strike: null, accent: false };
    return { price: p.monthly, strike: null, accent: false };
  };

  const reloadOffers = async () => {
    if (busy || billingBusy) return;
    setBusy("offers");
    try {
      setStoreProducts(groupOffers(await loadProducts()));
      setSelected({});
      setStoreError("");
    } catch (error) { setStoreError(error?.message || "Actualisation des offres impossible"); }
    finally { setBusy(null); }
  };

  const checkout = async (plan) => {
    setBusy(plan.id);
    try {
      if (native) {
        const productId = PRODUCT_BY_PLAN[plan.id];
        const storeProduct = selectedOffer(storeProducts[productId], selected[productId]);
        if (!storeProduct) throw new Error("Produit indisponible dans votre boutique");
        const result = await runBillingAction(async () => {
          const delivered = await buy(productId, storeProduct, user?.plan || "free");
          if (!delivered?.pending && !delivered?.cancelled && !delivered?.activationPending) {
            const updated = await refreshUser();
            const future = updated?.subscription?.subscriptions?.find((s) => s.pending_plan === plan.id);
            return { ...delivered, pending_plan: future?.pending_plan || delivered?.pending_plan };
          }
          return delivered;
        });
        if (result?.activationPending || result?.pending || result?.cancelled) {
          toast.info(result.activationPending ? ACTIVATION_PENDING_MESSAGE
            : result.pending ? "Paiement en attente de confirmation" : "Paiement annulé");
          setBusy(null);
          return;
        }
        setBusy(null);
        if (result?.pending_plan) toast.info(`Changement vers ${result.pending_plan.toUpperCase()} enregistré pour la prochaine échéance.`);
        else if (result?.active) { toast.success("Abonnement activé"); nav("/"); }
        else toast.info("Achat enregistré, aucun abonnement actif confirmé.");
        return;
      }
      throw new Error("Les achats sont disponibles dans l’application mobile");
    } catch (error) {
      toast.error(error?.response?.data?.detail || error?.message || t("paywall.checkout_error"));
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen px-4 pt-8 pb-28" style={{ background: "#050807" }} data-testid="paywall-screen">
      <div className="flex justify-center mb-6">
        <HitmaxWordmark size="text-3xl" />
      </div>

      <SubscriptionNotice />
      <div className="max-w-2xl mx-auto text-center">
        <h1 className="font-display text-3xl sm:text-4xl leading-tight tracking-wide">
          <Trans i18nKey="paywall.title" components={{ 1: <span style={{ color: GREEN }} />, br: <br className="hidden sm:block" /> }} />
        </h1>
        <p className="text-sm text-white/55 mt-3 max-w-xl mx-auto">
          {t("paywall.subtitle")}
        </p>

        <button onClick={() => nav("/performance")} className="block mx-auto mt-3 text-xs text-white/40 underline hover:text-white/60">
          Voir nos résultats vérifiés avant de t'abonner
        </button>

        {!native && <div className="inline-flex items-center gap-1 mt-6 p-1 rounded-full" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)" }} data-testid="paywall-billing-toggle">
          <button data-testid="toggle-monthly" onClick={() => setAnnual(false)}
            className={`px-5 py-2 rounded-full text-sm font-bold transition-all ${!annual ? "text-black" : "text-white/60"}`}
            style={!annual ? { background: GREEN } : {}}>{t("paywall.monthly")}</button>
          <button data-testid="toggle-annual" onClick={() => setAnnual(true)}
            className={`px-5 py-2 rounded-full text-sm font-bold transition-all flex items-center gap-1.5 ${annual ? "text-black" : "text-white/60"}`}
            style={annual ? { background: GREEN } : {}}>
            {t("paywall.annual")}
            <span className="text-[10px] font-black px-1.5 py-0.5 rounded-full" style={{ background: annual ? "rgba(0,0,0,0.2)" : GOLD, color: "#000" }}>-20%</span>
          </button>
        </div>}
        {native && <button onClick={reloadOffers} disabled={!!busy || billingBusy} className="block mx-auto mt-3 text-xs underline">Actualiser les offres</button>}
        {storeError && <p className="text-xs text-red-400 mt-4">{storeError}</p>}
      </div>

      <div className="max-w-2xl mx-auto mt-9 space-y-5">
        {PLANS.map((p) => {
          const accent = p.gold ? GOLD : GREEN;
          const features = t(p.featuresKey, { returnObjects: true });
          const pi = priceInfo(p);
          const pid = PRODUCT_BY_PLAN[p.id];
          const nativeProduct = selectedOffer(storeProducts[pid], selected[pid]);
          const terms = offerTerms(nativeProduct);
          const cardBg = p.gold
            ? "linear-gradient(155deg, rgba(217,179,106,0.12), rgba(217,179,106,0.02))"
            : p.highlight
            ? "linear-gradient(155deg, rgba(23,227,126,0.10), rgba(23,227,126,0.02))"
            : "rgba(255,255,255,0.03)";
          const cardBorder = p.gold
            ? "rgba(217,179,106,0.55)"
            : p.highlight
            ? "rgba(23,227,126,0.55)"
            : "rgba(255,255,255,0.1)";
          return (
            <div key={p.id} data-testid={`plan-${p.id}`}
              className={`relative rounded-2xl p-5 sm:p-6 transition-transform ${p.highlight ? "sm:scale-[1.015]" : ""}`}
              style={{
                background: cardBg,
                border: `1px solid ${cardBorder}`,
                boxShadow: p.gold ? "0 0 34px rgba(217,179,106,0.14)" : p.highlight ? "0 0 30px rgba(23,227,126,0.12)" : "none",
              }}>
              {p.badge && (
                <div className="absolute -top-3 left-5 flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest whitespace-nowrap"
                  style={{ background: p.badge.color, color: "#04120A" }}>
                  <p.badge.icon size={12} strokeWidth={2.5} /> {t(p.badge.textKey)}
                </div>
              )}

              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mt-1">
                <div className="min-w-0">
                  <h3 className="font-display text-2xl tracking-wider" style={{ color: accent }}>{p.name}</h3>
                  <div className="mt-2 flex items-end gap-2">
                    <span className="font-numeral font-black text-4xl" style={pi.accent && !native ? { color: accent } : {}}>
                      {native ? (terms?.price || "—") : `${fmt(pi.price)}€`}
                    </span>
                    {native ? <span className="text-white/45 text-xs mb-1.5">{terms ? ` / ${terms.period}` : ""}</span> : annual
                      ? <span className="text-white/45 text-xs mb-1.5">{t("paywall.per_year")}</span>
                      : pi.strike != null
                      ? <span className="text-white/40 text-base line-through mb-1">{fmt(pi.strike)}€</span>
                      : <span className="text-white/45 text-xs mb-1.5">{t("paywall.per_month")}</span>}
                  </div>
                  {native && <StoreOfferPicker offers={storeProducts[pid]} selected={nativeProduct} disabled={billingBusy || !!busy}
                    onChange={(key) => setSelected((prev) => ({ ...prev, [pid]: key }))} />}
                  {!annual && pi.strike != null && <p className="text-[11px] text-white/40 mt-0.5">{t("renew.then_regular", { defaultValue: "puis plein tarif" })}</p>}
                  {annual && (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span data-testid={`save-${p.id}`} className="inline-block text-[11px] font-extrabold px-2.5 py-1 rounded-full"
                        style={{ background: "rgba(23,227,126,0.12)", color: GREEN, border: `1px solid rgba(23,227,126,0.4)` }}>
                        {t("paywall.save_per_year", { amount: fmt(p.monthly * 12 - p.annual) })}
                      </span>
                      <span className="text-[11px] text-white/40">{t("paywall.billed_annually")}</span>
                    </div>
                  )}
                </div>

                <button data-testid={`subscribe-${p.id}`} onClick={() => checkout(p)} disabled={billingBusy || !!busy || user?.plan === p.id || (native && (!nativeProduct || !subscription || !!subscriptionError))}
                  className="shrink-0 w-full sm:w-auto sm:min-w-[168px] py-3 px-6 rounded-xl font-bold text-sm transition-all hover:scale-[1.02] disabled:opacity-60"
                  style={p.gold
                    ? { background: GOLD, color: "#04120A" }
                    : p.highlight
                    ? { background: GREEN, color: "#04120A" }
                    : { background: "rgba(255,255,255,0.08)", color: "#fff", border: "1px solid rgba(255,255,255,0.15)" }}>
                  {user?.plan === p.id ? "Forfait actuel" : busy === p.id ? t("paywall.redirecting")
                    : native && !nativeProduct ? "Indisponible"
                    : t("paywall.choose", { name: p.name })}
                </button>
              </div>

              <ul className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                {(Array.isArray(features) ? features : []).map((label, i) => {
                  const ok = p.featuresOk[i];
                  return (
                    <li key={i} className={`flex items-start gap-2 text-[13px] ${ok ? "text-white/80" : "text-white/30 line-through"}`}>
                      {ok ? <Check size={16} style={{ color: accent }} className="shrink-0 mt-0.5" />
                          : <X size={16} className="shrink-0 mt-0.5 text-white/25" />}
                      <span>{label}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>

      <div className="max-w-2xl mx-auto mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[12px] text-white/45">
        <span className="flex items-center gap-1.5"><ShieldCheck size={14} style={{ color: GREEN }} /> {native ? `Paiement sécurisé via ${platform() === "android" ? "Google Play" : "Apple"}` : t("paywall.secure")}</span>
        <span className="flex items-center gap-1.5"><Check size={14} style={{ color: GREEN }} /> {t("paywall.cancel_anytime")}</span>
        <span className="flex items-center gap-1.5"><Check size={14} style={{ color: GREEN }} /> {t("paywall.immediate")}</span>
      </div>
      {native && <p className="text-center text-[11px] text-white/35 mt-3">
        Paiement géré par {platform() === "android" ? "Google Play" : "Apple"}. Renouvellement automatique, résiliable depuis votre compte.
      </p>}

      <div className="text-center mt-7">
        <button data-testid="paywall-free-link"
          onClick={() => nav(freeMatch ? `/match/${freeMatch}` : "/")}
          className="text-[12px] text-white/35 hover:text-white/60 underline underline-offset-2 transition-colors">
          {t("paywall.free_link")}
        </button>
      </div>
    </div>
  );
}
